/**
 * Hamssah public URLs from admin.
 * After home+shop merge: one origin (hamssah.com). Shop home path = `/boutique`.
 */

function devHost(): string {
  return typeof window !== 'undefined' && window.location?.hostname
    ? window.location.hostname
    : 'localhost';
}

/** Unified public app — marketing + commerce. */
export function homeBaseUrl(): string {
  if (!import.meta.env.DEV) return 'https://hamssah.com';
  const configured = String((import.meta.env as Record<string, unknown>).VITE_HOME_URL ?? '').trim();
  if (configured) return configured.replace(/\/$/, '');
  return `http://${devHost()}:5174`;
}

/** Same origin as home after merge (kept for call-site clarity). */
export function storefrontBaseUrl(): string {
  if (!import.meta.env.DEV) return 'https://hamssah.com';
  const configured = String((import.meta.env as Record<string, unknown>).VITE_SHOP_URL ?? '').trim();
  if (configured) return configured.replace(/\/$/, '');
  return `http://${devHost()}:5174`;
}

function joinPublicPath(base: string, path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

/** Marketing preview (`/` landing, guides, diagnostic). */
export function homePreviewUrl(path = '/'): string {
  return joinPublicPath(homeBaseUrl(), path);
}

/** Shop preview — `/` maps to `/boutique`. */
export function shopPreviewUrl(path = '/'): string {
  const normalized = path === '/' || path === '' ? '/boutique' : path;
  return joinPublicPath(storefrontBaseUrl(), normalized);
}

const STOREFRONT_PLP_PATH_BY_TYPE: Record<string, string> = {
  COSMETIC: '/cosmetique',
  PARAPHARMACEUTICAL: '/parapharmacie',
  PHARMACEUTICAL: '/pharmacie',
  DEVICE: '/dispositifs',
  OTHER: '/autre',
};

export function storefrontProductTypePlpPath(productTypeCode: string | undefined | null): string {
  const c = String(productTypeCode ?? '').trim().toUpperCase();
  if (!c) return '/recherche';
  return STOREFRONT_PLP_PATH_BY_TYPE[c] ?? '/recherche';
}

export function storefrontProductTypeHref(productTypeCode: string | undefined | null): string | null {
  const c = String(productTypeCode ?? '').trim();
  if (!c) return null;
  return `${storefrontBaseUrl()}${storefrontProductTypePlpPath(c)}`;
}

/** Display path from type code, e.g. `PHARMACEUTICAL` → `/pharmaceutical`. */
export function storefrontProductTypeSlugLabel(productTypeCode: string | undefined | null): string {
  const c = String(productTypeCode ?? '').trim().toLowerCase();
  if (!c) return '/';
  return `/${c}`;
}

/** Storefront PLP filtered by marque (`markeId` query, same as home brand strip). */
export function storefrontMarkeProductsHref(markeId: string | undefined | null): string | null {
  const id = String(markeId ?? '').trim();
  if (!id) return null;
  const sp = new URLSearchParams();
  sp.set('markeId', id);
  return `${storefrontBaseUrl()}/recherche?${sp.toString()}`;
}

export function storefrontBesoinTileHref(opts: {
  productType: string;
  categorySlug?: string;
  concern?: string;
}): string | null {
  const typeHref = storefrontProductTypeHref(opts.productType);
  if (!typeHref) return null;
  const sp = new URLSearchParams();
  if (opts.categorySlug?.trim()) sp.set('categorySlug', opts.categorySlug.trim());
  if (opts.concern?.trim()) sp.set('concern', opts.concern.trim());
  const q = sp.toString();
  return q ? `${typeHref}?${q}` : typeHref;
}
